import React, { useState } from 'react'
import { CarryOutOutlined } from '@ant-design/icons'
import { ContextToolbar, Input, useTraversal } from '@guillotinaweb/react-gmi'
import { TabsPanelWithLock } from '../../components/tab-panel-with-lock'
import { Tree, Affix, Button, Drawer, message } from 'antd'
import { AuditLogTab } from '../../common/auditlog'
import { I18NField } from '../../components/i18nfield'
import { setLangs } from '../../consts'

function Taxonomy() {
  const ctx = useTraversal()
  const [children, setChildren] = useState(ctx.state.context.children)
  const title = ctx.state.context.title || ctx.state.context['@name']
  const [selected, setSelected] = useState()
  const [newName, setNewName] = useState(setLangs(''))
  const [newId, setNewId] = useState('')
  const [drawer, showDrawer] = useState(false)
  const [saving, setSaving] = useState(false)
  const affixStyle = {
    backgroundColor: 'white',
    display: 'flex',
    padding: 10,
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  }

  async function save() {
    try {
      const r = await ctx.client.patch(ctx.path, { children })
      if (r.status >= 400) throw new Error((await r.json()).message)
      setSaving(false)
      message.success(`Saved successfully.`)
      ctx.refresh()
    } catch (e) {
      setSaving(false)
      message.error(`Error saving. ${e.message}`)
    }
  }

  function getItemCursor() {
    const newChildren = JSON.parse(JSON.stringify(children)) // clone
    const pos = selected?.pos?.replace(/^0-0-/, '')?.split('-') || []
    let cursor = newChildren
    pos.forEach((i) => (cursor = (cursor?.children || cursor || [])[i]))
    return [cursor, newChildren]
  }

  function addNewChild(e) {
    e.preventDefault()
    const [cursor, newChildren] = getItemCursor()
    const newItem = { name: newName, id: newId, children: [] }
    const isArray = Array.isArray(cursor)
    const emptynames = setLangs('')

    if (isArray) {
      cursor.push(newItem)
    } else {
      cursor.children.push(newItem)
    }

    setSelected({
      ...selected,
      original: isArray ? { names: emptynames, children: cursor } : cursor,
    })
    setChildren(newChildren)
    setNewName(emptynames)
    setNewId('')
    showDrawer(false)
  }

  function loadData(lang) {
    function mapChildren(parentKey = '') {
      return (c) => {
        const key = `${parentKey}/${c.id}`
        return {
          title: c.name?.[lang],
          key,
          icon: <CarryOutOutlined />,
          children: (c.children || []).map(mapChildren(key)),
          original: c,
        }
      }
    }

    return [
      {
        title,
        icon: <CarryOutOutlined />,
        key: ctx.state.context['@name'],
        children: children.map(mapChildren()),
      },
    ]
  }

  return (
    <I18NField Widget={() => null}>
      {(lang) => (
        <>
          <Affix>
            <div style={affixStyle}>
              <h1 className="title is-size-4 is-primary">{title}</h1>
              <Button type="primary" disabled={saving} onClick={() => save()}>
                {saving ? 'Saving...' : 'Save taxonomy'}
              </Button>
            </div>
          </Affix>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ width: 250, backgroundColor: 'red' }}>
              <Tree
                showLine
                defaultExpandedKeys={[ctx.state.context['@name']]}
                onSelect={(id, e) => {
                  setSelected(
                    e.selected && e.node.pos !== '0-0' ? e.node : null
                  )
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                }}
                treeData={loadData(lang)}
              />
            </div>
            <div style={{ minWidth: 400 }}>
              <div style={affixStyle}>
                <h1
                  className="title is-size-5 is-primary"
                  style={{ margin: 0 }}
                >
                  {selected?.original?.name?.[lang] || <i>Select some child</i>}
                  {selected ? (
                    <small
                      style={{ color: 'grey' }}
                    >{` ${selected.key}`}</small>
                  ) : (
                    ''
                  )}
                </h1>
                <Button
                  type="default"
                  disabled={saving}
                  onClick={() => showDrawer(true)}
                >
                  Add child
                </Button>
              </div>
              {selected && (
                <>
                  <hr />
                  <h2 className="title is-size-6">Id</h2>
                  <Input
                    key={`id-${selected.key}`}
                    disabled
                    type="text"
                    value={selected.original?.id}
                  />
                  <hr />
                  <h2 className="title is-size-6">Name</h2>
                  <I18NField Widget={() => null}>
                    {(l) => (
                      <Input
                        type="text"
                        key={`name-${l}-${selected.key}`}
                        value={selected.original?.name?.[l]}
                        onChange={(value) => {
                          const [cursor, newChildren] = getItemCursor()
                          cursor.name[l] = value
                          setSelected({ ...selected, original: cursor })
                          setChildren(newChildren)
                        }}
                      />
                    )}
                  </I18NField>
                  {ctx.state.context['@name'] === 'categoria_productos' && (
                    <>
                      <h2 className="title is-size-6">URL</h2>
                      <I18NField Widget={() => null}>
                        {(l) => (
                          <Input
                            type="text"
                            key={`name-${l}-${selected.key}`}
                            value={selected.original?.url?.[l]}
                            onChange={(value) => {
                              const [cursor, newChildren] = getItemCursor()
                              if (!cursor.url) cursor.url = setLangs('')
                              cursor.url[l] = value
                              setSelected({ ...selected, original: cursor })
                              setChildren(newChildren)
                            }}
                          />
                        )}
                      </I18NField>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
          <Drawer
            title={`Add a child to ${
              selected ? selected?.original?.name?.[lang] || title : title
            }`}
            width={600}
            onClose={() => showDrawer(false)}
            visible={drawer}
            bodyStyle={{ paddingBottom: 80 }}
          >
            <form key={drawer} onSubmit={addNewChild}>
              <hr />
              <h2 className="title is-size-6">Id</h2>
              <Input
                value={newId}
                required
                key={`new-id-${selected?.key}`}
                onChange={setNewId}
              />
              <hr />
              <h2 className="title is-size-6">Name</h2>
              <I18NField Widget={() => null}>
                {(l) => (
                  <Input
                    value={newName[l]}
                    required
                    key={`new-name-${l}`}
                    onChange={(v) => setNewName((n) => ({ ...n, [l]: v }))}
                  />
                )}
              </I18NField>
              <div style={{ textAlign: 'right' }}>
                <button className="button">Add</button>
              </div>
            </form>
          </Drawer>
        </>
      )}
    </I18NField>
  )
}

const tabs = {
  Taxonomy,
  AuditLog: AuditLogTab,
}

const tabsPermissions = {
  Taxonomy: 'guillotina.ModifyContent',
  AuditLog: 'vinissimus.ViewLogs',
}

export function TaxonomyView(props) {
  const ctx = useTraversal()
  const calculated = ctx.filterTabs(tabs, tabsPermissions)
  return (
    <TabsPanelWithLock
      tabs={calculated}
      currentTab="Taxonomy"
      editableTabs={[]}
      rightToolbar={<ContextToolbar {...props} />}
      {...props}
    />
  )
}
